import {
  Badge,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { CompanyProfileService } from "../../api/companyProfile"

export default function CompanyProfileHistory({
  currentRevision,
}: {
  currentRevision: number
}) {
  const navigate = useNavigate()
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200")
  const [visibleCount, setVisibleCount] = useState(8)
  const query = useQuery({
    queryKey: ["companyProfile", "revisions"],
    queryFn: () => CompanyProfileService.getRevisions(),
    retry: 1,
  })

  if (query.isPending) {
    return (
      <Flex align="center" gap={3} py={6} justify="center">
        <Spinner size="sm" />
        <Text color="gray.500">Ładowanie historii zmian…</Text>
      </Flex>
    )
  }
  if (query.isError) {
    return (
      <Stack align="start" py={5}>
        <Text color="red.500">Nie udało się pobrać historii zmian.</Text>
        <Button size="sm" variant="outline" onClick={() => query.refetch()}>
          Spróbuj ponownie
        </Button>
      </Stack>
    )
  }
  if (query.data.length === 0) {
    return (
      <Text color="gray.500" py={5}>
        Brak zapisanych rewizji.
      </Text>
    )
  }

  return (
    <Stack spacing={3}>
      {query.data.slice(0, visibleCount).map((revision) => (
        <Flex
          key={revision.id}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          p={4}
          gap={4}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          direction={{ base: "column", md: "row" }}
        >
          <Stack spacing={1} minW={0}>
            <Flex gap={2} align="center" wrap="wrap">
              <Badge colorScheme="teal" borderRadius="full" px={3}>
                Rew. {revision.revisionNumber}
              </Badge>
              {revision.revisionNumber === currentRevision ? (
                <Badge colorScheme="green">Aktywna</Badge>
              ) : null}
              <Text fontWeight="semibold" noOfLines={1}>
                {revision.changeReason || "Brak opisu zmiany"}
              </Text>
            </Flex>
            <Text color="gray.500" fontSize="sm">
              {new Intl.DateTimeFormat("pl-PL", {
                dateStyle: "medium",
                timeStyle: "short",
              }).format(new Date(revision.createdAt))}
              {" · "}
              {revision.createdBy.fullName || "System"}
              {revision.createdBy.acronym
                ? ` (${revision.createdBy.acronym})`
                : ""}
            </Text>
          </Stack>
          <Button
            size="sm"
            variant="outline"
            flexShrink={0}
            onClick={() =>
              navigate({
                to: "/company-settings/revisions/$revisionId",
                params: { revisionId: revision.id },
              })
            }
          >
            Otwórz szczegóły
          </Button>
        </Flex>
      ))}
      {visibleCount < query.data.length ? (
        <Button
          alignSelf="center"
          variant="ghost"
          onClick={() => setVisibleCount((count) => count + 8)}
        >
          Pokaż więcej
        </Button>
      ) : null}
    </Stack>
  )
}
